import { useState } from 'react'
import { api } from '../api'

/**
 * Columns in the workbook that match no fund here — TECHNICAL_DESIGN.md §7.1.
 *
 * **Named rather than created.** The obvious thing is to make a portfolio for
 * every unrecognised column, and it is wrong in a way that only shows up later:
 * the sheet's shape is *detected* by matching its header against known codes, so
 * a column headed `EMDVLA` where the fund is `EMDVAL` would quietly become a new
 * portfolio carrying that column's limits, with nothing on any screen looking
 * out of place. Listing them is what puts the typo in front of somebody while it
 * is still cheap.
 *
 * **But not retyped either.** The codes are already in the file, and asking an
 * operator to key ten of them by hand reintroduces exactly the transcription
 * risk this whole path exists to remove. So they are offered, in one reviewed
 * step, with the currency stated once — which is the same shape as the layout
 * mapping: a person confirms a thing the machine proposed, once.
 *
 * **A base currency has to be asked for.** The header row does not carry one.
 * This workbook happens to have a *Base Currency* row, but that is this sheet's
 * shape rather than a property of workbooks, and defaulting to USD would be
 * inventing a fact about somebody's fund.
 *
 * Creating a portfolio is `admin` (§10.1) and migrating is `author`, so an
 * author sees the list and is told who can act on it. That is the separation
 * working rather than an obstacle: which funds the firm runs is an operational
 * fact, and it is not a drafting decision.
 */
export function UnmatchedPortfolios({
  codes,
  canCreate,
  onCreated,
}: {
  codes: string[]
  canCreate: boolean
  onCreated: () => void
}) {
  const [currency, setCurrency] = useState('USD')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const outstanding = codes.filter((code) => !done.includes(code))
  if (outstanding.length === 0 && done.length === 0) return null

  async function createAll() {
    setBusy(true)
    setError(null)
    try {
      for (const code of outstanding) {
        // One call per fund, so each is its own audit entry naming who created
        // which. A single bulk write would record ten funds as one act.
        await api.createPortfolio({ code, name: code, base_currency: currency })
        setDone((already) => [...already, code])
      }
      onCreated()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={outstanding.length ? 'warning' : 'callout'}>
      <h3>
        {outstanding.length > 0
          ? `${outstanding.length} column(s) match no fund here`
          : `${done.length} fund(s) created`}
      </h3>

      {outstanding.length > 0 && (
        <>
          <p>
            {outstanding.map((code) => (
              <code key={code} style={{ marginRight: '.5rem' }}>
                {code}
              </code>
            ))}
          </p>
          <p className="note">
            Check these against what you expect before creating anything. A
            mistyped column heading looks exactly like a fund this appliance has
            not been told about, and this is the only point at which the two are
            easy to tell apart. Their limits are skipped until a fund exists to
            carry them.
          </p>

          {canCreate ? (
            <>
              <label>
                Base currency for the new funds
                <input
                  value={currency}
                  maxLength={3}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
                <small>
                  Asked for rather than guessed: a header row does not carry a
                  currency, and defaulting one would be inventing a fact about
                  somebody’s fund. Create them in groups if they differ, or edit
                  afterwards.
                </small>
              </label>
              <div className="actions">
                <button disabled={busy || currency.length !== 3} onClick={() => void createAll()}>
                  {busy ? 'Creating…' : `Create ${outstanding.length} fund(s) from these columns`}
                </button>
              </div>
              <p className="note">
                Each is created with its code as its name, which you can change
                later. Creating a fund binds nothing — a portfolio with no rules
                constrains nothing until an approver puts a limit on it.
              </p>
            </>
          ) : (
            <p className="note">
              An administrator creates funds. Which funds the firm runs is an
              operational fact rather than a drafting decision, so it is not
              yours to do from here — send them this list.
            </p>
          )}
        </>
      )}

      {done.length > 0 && (
        <p className="note">
          Created: {done.join(', ')}. Upload the workbook again so the columns are
          matched against them.
        </p>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  )
}
